import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  eggProductionService,
  eggSalesService,
  eggStockService,
  eggPricesService,
} from '../services/apiServices';
import { queryKeys } from '../config/queryClient';
import { EggProduction, EggSale, EggStock, EggPrice, PaginatedResponse, Stats } from '../types';

// ============== Egg Production ==============

export const useEggProductions = (params?: any) => {
  return useQuery<PaginatedResponse<EggProduction>>({
    queryKey: queryKeys.eggProductionsList(params),
    queryFn: () => eggProductionService.getAll(params),
  });
};

export const useEggProduction = (id: number, enabled = true) => {
  return useQuery<EggProduction>({
    queryKey: queryKeys.eggProduction(id),
    queryFn: () => eggProductionService.getById(id),
    enabled,
  });
};

export const useEggProductionsStats = () => {
  return useQuery<Stats>({
    queryKey: queryKeys.eggProductionsStats,
    queryFn: eggProductionService.getStats,
  });
};

export const useEggProductionsTrends = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.eggProductionsTrends(params),
    queryFn: () => eggProductionService.getTrends(params),
  });
};

export const useEggProductionsTopPerformance = () => {
  return useQuery({
    queryKey: queryKeys.eggProductionsTopPerformance,
    queryFn: eggProductionService.getTopPerformance,
  });
};

export const useProductionCurve = (params?: { procurement_id?: number }) => {
  return useQuery({
    queryKey: ['egg-productions', 'production-curve', params],
    queryFn: () => eggProductionService.getProductionCurve(params),
  });
};

export const useCreateEggProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<EggProduction>) => eggProductionService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggProductions });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggProductionsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggStock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardMetrics });
    },
  });
};

export const useUpdateEggProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EggProduction> }) =>
      eggProductionService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggProduction(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggProductions });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggStock });
    },
  });
};

export const useDeleteEggProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => eggProductionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggProductions });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggProductionsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggStock });
    },
  });
};

// ============== Egg Sales ==============

export const useEggSales = (params?: any) => {
  return useQuery<PaginatedResponse<EggSale>>({
    queryKey: queryKeys.eggSalesList(params),
    queryFn: () => eggSalesService.getAll(params),
  });
};

export const useEggSale = (id: number, enabled = true) => {
  return useQuery<EggSale>({
    queryKey: queryKeys.eggSale(id),
    queryFn: () => eggSalesService.getById(id),
    enabled,
  });
};

export const useEggSalesStats = () => {
  return useQuery<Stats>({
    queryKey: queryKeys.eggSalesStats,
    queryFn: eggSalesService.getStats,
  });
};

export const useEggSalesPending = () => {
  return useQuery<EggSale[]>({
    queryKey: queryKeys.eggSalesPending,
    queryFn: eggSalesService.getPending,
  });
};

export const useEggSalesTrends = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.eggSalesTrends(params),
    queryFn: () => eggSalesService.getTrends(params),
  });
};

export const useCreateEggSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<EggSale>) => eggSalesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSales });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSalesStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggStock });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardMetrics });
    },
  });
};

export const useUpdateEggSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EggSale> }) =>
      eggSalesService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSale(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSales });
    },
  });
};

export const useDeleteEggSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => eggSalesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSales });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSalesStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggStock });
    },
  });
};

export const useMarkEggSalePaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => eggSalesService.markPaid(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSale(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSales });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggSalesPending });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

// ============== Egg Stock ==============

export const useEggStock = () => {
  return useQuery<EggStock[]>({
    queryKey: queryKeys.eggStock,
    queryFn: eggStockService.getAll,
  });
};

export const useEggStockSummary = () => {
  return useQuery({
    queryKey: queryKeys.eggStockSummary,
    queryFn: eggStockService.getSummary,
  });
};

export const useEggStockAlerts = () => {
  return useQuery({
    queryKey: queryKeys.eggStockAlerts,
    queryFn: eggStockService.getAlerts,
  });
};

// ============== Egg Prices ==============

export const useEggPrices = () => {
  return useQuery<EggPrice[]>({
    queryKey: queryKeys.eggPrices,
    queryFn: eggPricesService.getAll,
  });
};

export const useCurrentEggPrices = () => {
  return useQuery<EggPrice[]>({
    queryKey: queryKeys.eggPricesCurrent,
    queryFn: eggPricesService.getCurrent,
  });
};

export const useEggPriceHistory = (birdId: number, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.eggPricesHistory(birdId),
    queryFn: () => eggPricesService.getAll(),
    enabled,
  });
};

export const useCreateEggPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<EggPrice>) => eggPricesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggPrices });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggPricesCurrent });
    },
  });
};

export const useSetCurrentEggPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => eggPricesService.setCurrent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eggPrices });
      queryClient.invalidateQueries({ queryKey: queryKeys.eggPricesCurrent });
    },
  });
};
